import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/apiClient";
import {
  PageHead, Card, Pill, StatusPill, FilterBar, TextInput, Select, Loading, Err, tableStyles, EmptyState,
} from "./_shared";

export default function AdminWorkspaces() {
  const [items, setItems] = useState(null);
  const [err, setErr] = useState("");
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [type, setType] = useState("");

  useEffect(() => {
    const url = new URLSearchParams();
    if (q) url.set("q", q);
    if (status) url.set("status", status);
    if (type) url.set("ws_type", type);
    const qs = url.toString();
    api.get(`/api/admin/workspaces${qs ? "?" + qs : ""}`).then((d) => setItems(d.items)).catch((e) => setErr(e.message));
  }, [q, status, type]);

  const summary = useMemo(() => {
    if (!items) return null;
    const total = items.length;
    const active = items.filter((w) => w.status === "active").length;
    const credits = items.reduce((sum, w) => sum + (w.available_balance || 0), 0);
    return { total, active, credits };
  }, [items]);

  if (err) return <Err msg={err} />;

  return (
    <div data-testid="admin-workspaces">
      <PageHead
        title="Workspaces"
        sub={summary ? `${summary.total} total · ${summary.active} active · ${summary.credits.toLocaleString()} credits in float` : "Loading…"}
      />

      <FilterBar>
        <TextInput value={q} onChange={setQ} placeholder="Search by name or ID…" data-testid="ws-search" />
        <Select value={status} onChange={setStatus} placeholder="All statuses"
          options={["active", "suspended", "deleted"]} data-testid="ws-filter-status" />
        <Select value={type} onChange={setType} placeholder="All types"
          options={["personal", "team", "agency", "private_label", "system"]} data-testid="ws-filter-type" />
      </FilterBar>

      {!items ? <Loading /> : items.length === 0 ? (
        <EmptyState title="No workspaces match these filters" description="Try clearing the search or status filters." />
      ) : (
        <Card pad={0} style={{ overflow: "hidden" }}>
          <table style={tableStyles.table}>
            <thead>
              <tr>
                <th style={tableStyles.th}>Workspace</th>
                <th style={tableStyles.th}>Type</th>
                <th style={tableStyles.th}>Status</th>
                <th style={{ ...tableStyles.th, textAlign: "right" }}>Members</th>
                <th style={{ ...tableStyles.th, textAlign: "right" }}>Credits</th>
                <th style={{ ...tableStyles.th, textAlign: "right" }}>Jobs</th>
                <th style={{ ...tableStyles.th, textAlign: "right" }}>Failed</th>
                <th style={tableStyles.th} />
              </tr>
            </thead>
            <tbody>
              {items.map((w) => {
                const health = w.failed_jobs > 0 && w.jobs_this_month > 0 && (w.failed_jobs / w.jobs_this_month) > 0.4
                  ? "warn" : "ok";
                return (
                  <tr key={w.id} data-testid={`workspace-row-${w.id}`} style={{ borderTop: "1px solid var(--pl-border)" }}>
                    <td style={tableStyles.td}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ width: 7, height: 7, borderRadius: 999, background: health === "ok" ? "#34d399" : "#fcd34d" }} />
                        <strong>{w.name}</strong>
                      </div>
                      <div style={{ fontSize: 10, color: "var(--pl-text-muted)", fontFamily: "JetBrains Mono, monospace", marginTop: 2 }}>{w.id}</div>
                    </td>
                    <td style={tableStyles.td}><Pill>{w.workspace_type}</Pill></td>
                    <td style={tableStyles.td}><StatusPill s={w.status} /></td>
                    <td style={{ ...tableStyles.td, textAlign: "right", fontFamily: "JetBrains Mono, monospace" }}>{w.member_count ?? 0}</td>
                    <td style={{ ...tableStyles.td, textAlign: "right", fontWeight: 800, color: "var(--pl-aqua)", fontFamily: "JetBrains Mono, monospace" }}>{(w.available_balance ?? 0).toLocaleString()}</td>
                    <td style={{ ...tableStyles.td, textAlign: "right", fontFamily: "JetBrains Mono, monospace" }}>{w.jobs_this_month ?? 0}</td>
                    <td style={{ ...tableStyles.td, textAlign: "right", fontFamily: "JetBrains Mono, monospace", color: w.failed_jobs > 0 ? "#fda4af" : "var(--pl-text-muted)" }}>{w.failed_jobs ?? 0}</td>
                    <td style={{ ...tableStyles.td, textAlign: "right" }}>
                      <Link data-testid={`workspace-open-${w.id}`} to={`/admin/workspaces/${w.id}`} style={{ color: "var(--pl-aqua)", fontWeight: 800, fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase" }}>Open →</Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}
